import { Button } from '../../components/ui/button';
import { FileText, Download, Loader2 } from 'lucide-react';

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

const displayValue = (field, value) => {
  if (value === undefined || value === null || String(value).trim() === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (field.type === 'duration' || (typeof value === 'object' && value !== null)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const parts = Object.entries(value)
        .filter(([, v]) => String(v ?? '').trim() !== '')
        .map(([u, v]) => `${v} ${u}`);
      return parts.length ? parts.join(', ') : '-';
    }
  }
  if (field.type === 'checkbox') return value ? 'Oui' : 'Non';
  if (field.type === 'montant_total') {
    const n = parseFloat(String(value).replace(',', '.'));
    return Number.isFinite(n) ? `${n.toLocaleString('fr-FR')} EUR` : String(value);
  }
  return String(value);
};

/**
 * Rendu en lecture seule d'une vente basee sur son template_snapshot + form_values.
 * Utilise par AdminSales et MySales pour afficher les details FDV dynamiquement.
 */
export default function FDVSnapshotView({ snapshot, formValues = {}, documents = [], onDownload, downloadingId }) {
  if (!snapshot || !snapshot.steps) {
    return <p className="text-slate-500 text-sm" data-testid="fdv-snapshot-empty">Aucune donnee de formulaire disponible.</p>;
  }
  const steps = sortByOrder(snapshot.steps);

  return (
    <div className="space-y-6" data-testid="fdv-snapshot-view">
      {steps.map((step) => {
        const fields = sortByOrder(step.fields).filter((f) => f.type !== 'file');
        if (fields.length === 0) return null;
        return (
          <div key={step.step_id} className="border-t pt-4">
            <p className="font-semibold text-indigo-600 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" /> {step.title}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
              {fields.map((f) => (
                <div key={f.field_id} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                  <p className="text-sm text-slate-500">{f.label}</p>
                  <p className={`font-medium ${f.type === 'montant_total' ? 'text-emerald-600' : ''}`}>
                    {displayValue(f, formValues[f.key])}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="border-t pt-4">
        <p className="font-semibold mb-3">Documents attaches ({documents.length})</p>
        {documents.length === 0 ? (
          <p className="text-slate-500 text-sm">Aucun document</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-sm">{doc.filename}</p>
                    <p className="text-xs text-slate-500">{doc.doc_type || doc.type}</p>
                  </div>
                </div>
                {onDownload && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDownload(doc.doc_id, doc.filename)}
                    disabled={downloadingId === doc.doc_id}
                    data-testid={`snapshot-download-${doc.doc_id}`}
                  >
                    {downloadingId === doc.doc_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Download className="w-4 h-4 mr-1" /> Telecharger</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
